import { User, getToken } from "helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environment } from "environment";

// Define a service using a base URL and expected endpoints
export const profileApi = createApi({
  reducerPath: "profileApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${environment.dev.baseUrl}/auth/user/profile`,
    prepareHeaders: (headers) => {
      headers.set("authorization-token", getToken() || "");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getProfile: builder.query<User, GetProfileArgs>({
      query: () => ({
        url: "",
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    updateProfile: builder.mutation<User, UpdateProfileArgs>({
      query: ({
        first_name,
        last_name,
        phone_number,
        address,
        municipality,
        language,
        confirmation_status,
        logged_in,
      }) => ({
        url: "",
        method: "POST",
        body: {
          first_name,
          last_name,
          phone_number,
          address,
          municipality,
          language,
          confirmation_status,
          logged_in,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    setLoggedInTrue: builder.mutation<User, void>({
      query: () => ({
        url: "",
        method: "POST",
        body: { logged_in: true },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    setLoggedInFalse: builder.mutation<User, void>({
      query: () => ({
        url: "",
        method: "POST",
        body: { logged_in: false },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    attachPicture: builder.mutation<User, AttachPictureArgs>({
      query: ({ fileName, base64Data }) => ({
        url: "attach-picture",
        method: "PUT",
        body: base64Data,
        headers: {
          "file-name": fileName,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    changePassword: builder.mutation<unknown, ChangePassword>({
      query: ({ oldPassword, newPassword, confirmPassword }) => ({
        url: "change-password",
        method: "POST",
        body: {
          oldPassword,
          newPassword,
          confirmPassword,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    forgotPassword: builder.mutation<unknown, ForgotPassword>({
      query: ({ username, confirmationCode, newPassword, confirmPassword }) => ({
        url: "confirm-forgot-password",
        method: "POST",
        body: {
          username,
          confirmationCode,
          newPassword,
          confirmPassword,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    getAttributeCode: builder.query<unknown, Attribute>({
      query: (attribute) => ({
        url: `generate-attribute-verification-code/${attribute}`,
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    verifyAttribute: builder.mutation<unknown, VerifyAttributeArgs>({
      query: ({ attribute, code }) => ({
        url: "verify-attribute",
        method: "POST",
        body: {
          attribute,
          code,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),
  }),
});

// Export hooks for usage in functional components, which are auto-generated based on the defined endpoints
export const {
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
  useAttachPictureMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useGetAttributeCodeQuery,
  useLazyGetAttributeCodeQuery,
  useVerifyAttributeMutation,
  useSetLoggedInFalseMutation,
  useSetLoggedInTrueMutation,
  usePrefetch: useProfilePrefetch,
} = profileApi;

interface UpdateProfileArgs
  extends Partial<
    Pick<
      User,
      "first_name" | "last_name" | "phone_number" | "address" | "municipality" | "language" | "confirmation_status"
    >
  > {
  logged_in?: boolean;
}

interface ChangePassword {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ForgotPassword {
  username: string;
  confirmationCode: string;
  confirmPassword: string;
  newPassword: string;
}

type GetProfileArgs = void | undefined;

type Attribute = "email" | "phone_number";

interface VerifyAttributeArgs {
  attribute: Attribute;
  code: string;
}

interface AttachPictureArgs {
  fileName: string;
  base64Data: string;
}
